import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { User, UserManager, UserManagerSettings } from 'oidc-client';
import { createAction, props, Store } from '@ngrx/store';
import { LocalStorageService } from '../../core/local-storage/local-storage.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthorizeService {
  private readonly authNavStatusSource = new ReplaySubject<boolean>(1);
  readonly authNavStatus$: Observable<boolean> =
    this.authNavStatusSource.asObservable();

  private readonly userManagerSettings: UserManagerSettings = {
    authority: environment.identityserver4Url,
    client_id: 'mojito_vercel',
    redirect_uri: `${window.location.origin}${environment.redirect_uri}`,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'id_token token',
    scope: 'mojito_api MyClientId_api openid profile email green_api odata_api',
    filterProtocolClaims: true,
    loadUserInfo: true,
  };

  private readonly manager: UserManager;
  private _user: User | null = null;

  constructor(private readonly store: Store<{}>) {
    this.manager = new UserManager(this.userManagerSettings);
    this.initializeAuthentication();
  }

  async login(): Promise<void> {
    return this.manager.signinRedirect();
  }

  async completeAuthentication(): Promise<void> {
    try {
      this.user = await this.manager.signinRedirectCallback();

      if (this.user.profile.name) {
        this.dispatchLoginAction(this.user.profile.name);
        this.updateAuthStatus();
      }
    } catch (error) {
      console.error('Authentication completion failed:', error);
      await this.signout();
    }
  }

  async signout(): Promise<void> {
    LocalStorageService.logOut();
    await this.manager.signoutRedirect();
  }

  isAuthenticated(): boolean {
    return Boolean(this.user && !this.user.expired);
  }

  getAccessToken(): string {
    return this.user?.access_token ?? '';
  }

  getUserName(): string {
    return this.user?.profile.name ?? '';
  }

  getUserEmail(): string {
    return this.user?.profile.email ?? '';
  }

  get user(): User | null {
    return this._user;
  }

  set user(value: User | null) {
    this._user = value;
  }

  isAdmin(): boolean {
    return environment.userAdmin.includes(this.getUserName());
  }

  private async initializeAuthentication(): Promise<void> {
    try {
      this.user = await this.manager.getUser();
      this.updateAuthStatus();

      if (this.user?.profile.name) {
        this.dispatchLoginAction(this.user.profile.name);
      }
    } catch (error) {
      console.error('Authentication initialization failed:', error);
      this.authNavStatusSource.next(false);
    }
  }

  private dispatchLoginAction(userName: string): void {
    this.store.dispatch(
      createAction(
        '[Login] logIn',
        props<{ userName: string }>()
      )({
        userName: userName.trim(),
      })
    );
  }

  private updateAuthStatus(): void {
    this.authNavStatusSource.next(this.isAuthenticated());
  }
}
